import React, { useCallback, useRef, useState } from "react";

interface ResizeHandleProps {
  currentHeight: number;
  minHeight?: number;
  maxHeight?: number;
  onResize: (newHeight: number) => void;
}

/**
 * Composant de poignée de redimensionnement pour les blocs BlockNote.
 * Affiche une barre horizontale en bas du bloc que l'utilisateur peut tirer
 * pour modifier la hauteur.
 */
export const ResizeHandle: React.FC<ResizeHandleProps> = ({
  currentHeight,
  minHeight = 100,
  maxHeight = 800,
  onResize,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      startYRef.current = clientY;
      startHeightRef.current = currentHeight;
      setIsDragging(true);

      const handleMouseMove = (moveEvent: MouseEvent | TouchEvent) => {
        const currentY =
          "touches" in moveEvent
            ? moveEvent.touches[0].clientY
            : moveEvent.clientY;
        const delta = currentY - startYRef.current;
        const newHeight = Math.min(
          maxHeight,
          Math.max(minHeight, startHeightRef.current + delta)
        );
        onResize(newHeight);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleMouseMove);
        document.removeEventListener("touchend", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleMouseMove);
      document.addEventListener("touchend", handleMouseUp);
    },
    [currentHeight, minHeight, maxHeight, onResize]
  );

  return (
    <div
      className={`
        absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize
        flex items-center justify-center
        transition-opacity duration-200
        ${isDragging ? "opacity-100" : "opacity-0 group-hover:opacity-100"}
      `}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      style={{
        background: isDragging
          ? "linear-gradient(to bottom, transparent, var(--accent, #3b82f6)20)"
          : "linear-gradient(to bottom, transparent, var(--arc-border, #e5e7eb))",
      }}
    >
      {/* Indicateur de grip */}
      <div
        className={`
          w-12 h-1 rounded-full
          transition-colors duration-200
          ${isDragging ? "bg-accent" : "bg-content-tertiary"}
        `}
        style={{
          backgroundColor: isDragging
            ? "var(--accent, #3b82f6)"
            : "var(--content-tertiary, #9ca3af)",
        }}
      />
    </div>
  );
};

export default ResizeHandle;
